import React, { useState, useEffect, useCallback } from "react";
import * as Sentry from "@sentry/react";
//#region redux
import { useDispatch } from "react-redux";
import { displayErrorMessage, closeDialog } from "store/actions/utilActions";
//#endregion
//#region types
import {
  GrossSalaryChangeCompanyResult,
  GrossSalaryChangeContextState,
  GrossSalaryChangeProviderProps,
} from "models/propTypes/wintSalaryPropTypes";
import { WintSuperkollAPIModelWintSalary, WintSuperkollAPIModelWintSalaryReport } from "wint-api-client/dist/types";
//#endregion
//#region components
import { GrossSalaryChangeContext } from "./index";
import { useWintSalaryService } from "serviceHooks";
import { LocalizationHelperInstance } from "utils/helpers";
import { TranslationKeys } from "translations";
import { SalaryConstants } from "utils/constants";
//#endregion

export const GrossSalaryChangeProvider: React.FC<GrossSalaryChangeProviderProps> = ({
  personId,
  yearMonth,
  zeroGrossSalaryChange = false,
  children,
}) => {
  const [data, setData] = useState<WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRow | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [companyResult, setCompanyResult] = useState<GrossSalaryChangeCompanyResult>({});
  const [minimumTargetGrossSalaryAmount, setMinimumTargetGrossSalaryAmount] =
    useState<WintSuperkollAPIModelWintSalary.WintSalaryMinimumTargetGrossSalaryAmountResult>({
      monthlySalaryAmount: 0,
      taxTableMinimumAmount: 0,
      oneTimeTaxMinimumAmount: 0,
      alreadyPaidOutNetAmount: 0,
    });

  const { getPersonSalaryReport, getMinimumTargetGrossSalaryAmount, calculateGrossSalaryWithTarget } =
    useWintSalaryService();

  const dispatch = useDispatch();

  const onError = (error: any) => {
    if (!error) return;
    if (error instanceof Error) {
      Sentry.withScope((scope) => {
        scope.setExtras({ personId, yearMonth, zeroGrossSalaryChange });
        Sentry.captureException(error);
      });
    }
    dispatch(
      displayErrorMessage(
        LocalizationHelperInstance.getFormattedMessage(error?.message ?? TranslationKeys.Errors.SomethingWentWrong),
        {
          isGlobal: true,
        }
      )
    );
  };

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      if (zeroGrossSalaryChange) {
        const minTargetAmounts = await getMinimumTargetGrossSalaryAmount(personId, yearMonth);
        if (minTargetAmounts.alreadyPaidOutNetAmount > 0) {
          dispatch(
            displayErrorMessage(
              LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Salary.ZeroGrossSalaryChangeDialogResetSalaryError
              ),
              {
                isGlobal: true,
              }
            )
          );
          dispatch(closeDialog(SalaryConstants.ZeroGrossSalaryChangeDialogId));
          return;
        }
        const result = await calculateGrossSalaryWithTarget(personId, yearMonth, 0);
        setData(result.salaryReportRow);
      } else {
        const [initialPersonData, minTargetAmount] = await Promise.all([
          getPersonSalaryReport(personId, yearMonth),
          getMinimumTargetGrossSalaryAmount(personId, yearMonth),
        ]);

        setMinimumTargetGrossSalaryAmount(minTargetAmount);
        setData(initialPersonData);
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  }, [personId, yearMonth, zeroGrossSalaryChange]);

  const refetchWithCalculation = useCallback(
    async (grossSalary: number, useOneTimeTax?: boolean) => {
      setLoading(true);
      try {
        const result = await calculateGrossSalaryWithTarget(personId, yearMonth, grossSalary, useOneTimeTax);

        const companyResult: GrossSalaryChangeCompanyResult = {
          remainingNetAmount: result.remainingNetAmount,
          alreadyPaidOutNetAmount: result.alreadyPaidOutNetAmount,
          privateTax: result.privateTax,
          socialFees: result.socialFees,
          totalCostForCompany: result.totalCostForCompany,
        };

        setCompanyResult(companyResult);
        setData(result.salaryReportRow);
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    },
    [personId, yearMonth]
  );

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const isTaxTypeSelectionEnabled = useCallback(
    (grossSalary?: number): boolean => {
      return (
        grossSalary !== undefined &&
        grossSalary >=
          SalaryConstants.GrossSalaryChangeTaxTypeSelectionThreshold +
            minimumTargetGrossSalaryAmount.monthlySalaryAmount
      );
    },
    [minimumTargetGrossSalaryAmount.monthlySalaryAmount]
  );

  const value: GrossSalaryChangeContextState = {
    personData: data,
    minimumTargetGrossSalaryAmount,
    loading,
    companyResult,
    fetchInitialData,
    refetchWithCalculation,
    isTaxTypeSelectionEnabled,
  };

  return <GrossSalaryChangeContext.Provider value={value}>{children}</GrossSalaryChangeContext.Provider>;
};
